import React, { useState, useEffect, useCallback, useMemo } from 'react';
import apiService from '../services/apiService';
import Dialogo from '../components/Dialogo';
import { BannerBody } from '../utils/bannerMarkdown';
import './Termos.css';

/**
 * Termo de aceite do app (role = admin).
 *
 * Três abas sobre o mesmo escopo escolhido no topo (Global / Empresa / Loja):
 *   Versões   -> o TEXTO. Rascunho edita; publicar aposenta a vigente do MESMO escopo; publicada é
 *                imutável (para mudar, cria-se outra versão a partir dela). Cascata no app:
 *                loja > empresa > global.
 *   Política  -> COMO o termo se comporta no escopo (off | required | explicit_refusal). Também em
 *                cascata. Sem política em nenhum nível = off: publicar um texto NÃO liga nada.
 *   Relatório -> quem aceitou, quem não, quem recusou, por empresa (e loja). Exporta CSV.
 *
 * Esta tela NÃO fica dentro de Configurações porque o termo tem escopo GLOBAL, e o seletor de
 * Configurações exige uma empresa.
 *
 * A prévia usa o mesmo parser markdown-lite dos comunicados (BannerBody): é o dialeto que o app
 * renderiza nativo. A substituição das variáveis repete a regra da API (render_terms) — se uma mudar,
 * a outra tem de mudar junto.
 */

const ENFORCEMENT_LABEL = {
    off: 'Desligado',
    required: 'Obrigatório (sem recusa)',
    explicit_refusal: 'Obrigatório com recusa registrada',
};
const STATUS_LABEL = { draft: 'Rascunho', published: 'Vigente', retired: 'Aposentada' };
const STATE_LABEL = { accepted: 'Aceitou', pending: 'Pendente', declined: 'Recusou', off: 'Não exigido' };

const BODY_EXEMPLO = `**TERMO DE CIÊNCIA E AUTORIZAÇÃO DE USO DE VOZ E DADOS**

Pelo presente instrumento, eu, {{fullname}}{{#cpf}}, portador(a) do CPF nº {{cpf}}{{/cpf}}, na qualidade de colaborador(a) da empresa {{company}}, declaro estar ciente e de acordo com as condições abaixo.

**1. OBJETO E FINALIDADE**
Autorizo a captação, gravação e processamento da minha voz durante os atendimentos realizados na loja {{store}}.`;

// Mesma regra de terms_routes.render_terms: o bloco do CPF some inteiro sem CPF; variável
// desconhecida fica como está (o admin enxerga o erro de digitação na prévia).
function renderTermo(body, { fullname, company, store, cpf }) {
    let out = body || '';
    out = out.replace(/\{\{#cpf\}\}([\s\S]*?)\{\{\/cpf\}\}/g, (_, inner) => (cpf ? inner : ''));
    const vals = { fullname, company, store, cpf: cpf || '' };
    Object.keys(vals).forEach((k) => { out = out.split(`{{${k}}}`).join(vals[k] || ''); });
    return out;
}

// timestamp naive em UTC vindo da API -> horário de Brasília. Aparar a fração a 3 dígitos: o Safari
// não parseia microssegundos.
function fmtData(iso) {
    if (!iso) return '—';
    const limpo = String(iso).replace(/(\.\d{3})\d+/, '$1');
    const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(limpo) ? limpo : `${limpo}Z`);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

function csvCell(v) {
    const s = v == null ? '' : String(v);
    return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function baixarCsv(nome, linhas) {
    // BOM + ';' para o Excel em pt-BR abrir com acento e colunas certas
    const conteudo = '﻿' + linhas.map((l) => l.map(csvCell).join(';')).join('\r\n');
    const url = URL.createObjectURL(new Blob([conteudo], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = nome;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

const erroDe = (e, padrao) => e?.response?.data?.detail || e?.response?.data?.error || e?.message || padrao;

function Termos({ user }) {
    const userId = user?.user_id;

    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4500);
    }, []);

    const [aba, setAba] = useState('versoes');

    // --- escopo escolhido no topo
    const [companies, setCompanies] = useState([]);
    const [companyId, setCompanyId] = useState('');   // '' = global
    const [stores, setStores] = useState([]);
    const [storeId, setStoreId] = useState('');       // '' = toda a empresa

    useEffect(() => {
        (async () => {
            try {
                const r = await apiService.getCompaniesByUser(userId);
                setCompanies((r?.companies || []).map((c) => ({ id: c.id, name: c.name, subdomain: c.subdomain })));
            } catch (e) { notify(erroDe(e, 'Erro ao carregar empresas'), 'error'); }
        })();
    }, [userId, notify]);

    const company = useMemo(() => companies.find((c) => String(c.id) === String(companyId)), [companies, companyId]);

    useEffect(() => {
        setStoreId('');
        setStores([]);
        if (!company?.subdomain) return undefined;
        let vivo = true;
        (async () => {
            try {
                const lojas = await apiService.promptEditorGetStores(company.subdomain);
                if (vivo) setStores(Array.isArray(lojas) ? lojas : []);
            } catch (e) { if (vivo) notify(erroDe(e, 'Erro ao carregar lojas'), 'error'); }
        })();
        return () => { vivo = false; };
    }, [company, notify]);

    const scope = useMemo(() => ({
        company_id: companyId ? Number(companyId) : null,
        store_id: companyId && storeId ? Number(storeId) : null,
    }), [companyId, storeId]);

    const lojaNome = useCallback((cid, sid) => {
        if (sid == null) return null;
        if (String(cid) === String(companyId)) {
            const l = stores.find((s) => s.id === sid);
            if (l) return l.display_name || l.name;
        }
        return `loja #${sid}`;
    }, [companyId, stores]);

    const empresaNome = useCallback((cid) => {
        const c = companies.find((x) => x.id === cid);
        return c ? c.name : `empresa #${cid}`;
    }, [companies]);

    const escopoLabel = useCallback((cid, sid) => {
        if (cid == null) return 'Global';
        if (sid == null) return `Empresa — ${empresaNome(cid)}`;
        return `Loja — ${empresaNome(cid)} / ${lojaNome(cid, sid)}`;
    }, [empresaNome, lojaNome]);

    const mesmoEscopo = (cid, sid) => (cid ?? null) === scope.company_id && (sid ?? null) === scope.store_id;

    return (
        <div className="termos-page">
            <div className="termos-header">
                <div>
                    <h1>Termo de aceite</h1>
                    <p className="termos-sub">
                        Texto que o vendedor aceita no app. O que vale para a loja é, nesta ordem, o da
                        <b> loja</b>, o da <b>empresa</b> e o <b>global</b>. Nada é exigido até existir uma
                        política ligada para o escopo (aba Política).
                    </p>
                </div>
                <div className="termos-escopo">
                    <label>
                        Empresa
                        <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                            <option value="">Global (todas as empresas)</option>
                            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </label>
                    <label>
                        Loja
                        <select value={storeId} onChange={(e) => setStoreId(e.target.value)} disabled={!companyId}>
                            <option value="">{companyId ? 'Toda a empresa' : '—'}</option>
                            {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                        </select>
                    </label>
                </div>
            </div>

            <nav className="termos-abas">
                {[['versoes', 'Versões'], ['politica', 'Política'], ['relatorio', 'Relatório']].map(([k, l]) => (
                    <button key={k} className={`termos-aba ${aba === k ? 'termos-aba-ativa' : ''}`} onClick={() => setAba(k)}>
                        {l}
                    </button>
                ))}
            </nav>

            {aba === 'versoes' && (
                <AbaVersoes
                    scope={scope} company={company} stores={stores} notify={notify}
                    escopoLabel={escopoLabel} mesmoEscopo={mesmoEscopo}
                />
            )}
            {aba === 'politica' && (
                <AbaPolitica scope={scope} notify={notify} escopoLabel={escopoLabel} mesmoEscopo={mesmoEscopo} />
            )}
            {aba === 'relatorio' && (
                <AbaRelatorio scope={scope} company={company} notify={notify} />
            )}

            {toast && <div className={`termos-toast ${toast.type}`}>{toast.msg}</div>}
        </div>
    );
}

/* ============================================================================================ */
/* Versões                                                                                      */
/* ============================================================================================ */
function AbaVersoes({ scope, company, stores, notify, escopoLabel, mesmoEscopo }) {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [onlyScope, setOnlyScope] = useState(true);

    // editor: null = fechado; { id|null, title, body_md, status, readOnly, company_id, store_id }
    const [edit, setEdit] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirm, setConfirm] = useState(null); // { kind: 'publish'|'delete', v }
    const [simCpf, setSimCpf] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiService.termsVersionsList();
            setVersions(r?.versions || []);
        } catch (e) { notify(erroDe(e, 'Erro ao carregar versões'), 'error'); }
        finally { setLoading(false); }
    }, [notify]);
    useEffect(() => { load(); }, [load]);

    // trocar de escopo no topo fecha um editor que estava aberto em outro escopo
    useEffect(() => { setEdit(null); }, [scope.company_id, scope.store_id]);

    const lista = useMemo(
        () => (onlyScope ? versions.filter((v) => mesmoEscopo(v.company_id, v.store_id)) : versions),
        [versions, onlyScope, mesmoEscopo],
    );

    const vigenteDoEscopo = versions.find((v) => v.status === 'published' && mesmoEscopo(v.company_id, v.store_id));

    const novo = (base) => setEdit({
        id: null, status: 'draft', readOnly: false,
        company_id: scope.company_id, store_id: scope.store_id,
        title: base?.title || 'Termo de ciência e autorização de uso de voz e dados',
        body_md: base?.body_md || BODY_EXEMPLO,
    });

    const abrir = (v) => setEdit({
        id: v.id, status: v.status, readOnly: v.status !== 'draft', version: v.version,
        company_id: v.company_id, store_id: v.store_id, title: v.title, body_md: v.body_md,
        sha: v.body_sha256, accepted_users: v.accepted_users,
    });

    const salvar = async () => {
        setSaving(true);
        try {
            const r = await apiService.termsVersionSave({
                id: edit.id || undefined, company_id: edit.company_id, store_id: edit.store_id,
                title: edit.title, body_md: edit.body_md,
            });
            notify('Rascunho salvo', 'success');
            await load();
            setEdit((p) => ({ ...p, id: r.id }));
        } catch (e) { notify(erroDe(e, 'Erro ao salvar'), 'error'); }
        finally { setSaving(false); }
    };

    const executarConfirmacao = async () => {
        setSaving(true);
        try {
            if (confirm.kind === 'publish') {
                await apiService.termsVersionPublish({ id: confirm.v.id });
                notify('Versão publicada', 'success');
            } else {
                await apiService.termsVersionDelete({ id: confirm.v.id });
                notify('Rascunho apagado', 'success');
            }
            setConfirm(null);
            setEdit(null);
            await load();
        } catch (e) { notify(erroDe(e, 'Erro na operação'), 'error'); }
        finally { setSaving(false); }
    };

    // Publicar exige o rascunho SALVO (o que vai ao ar é o que está no banco, não o que está na tela)
    const rascunhoSalvo = edit && edit.id && !edit.readOnly;
    const nomeLoja = (sid) => (stores.find((s) => s.id === sid)?.display_name) || `loja #${sid}`;

    const prev = edit ? renderTermo(edit.body_md, {
        fullname: 'Maria de Souza',
        company: edit.company_id != null ? (company && company.id === edit.company_id ? company.name : 'Empresa') : 'Nome da empresa',
        store: edit.store_id != null ? nomeLoja(edit.store_id) : 'Nome da loja',
        cpf: simCpf ? '529.982.247-25' : '',
    }) : '';

    return (
        <div className="termos-corpo">
            <div className="termos-barra">
                <button className="termos-btn termos-btn-primario" onClick={() => novo(vigenteDoEscopo)}>
                    + Nova versão neste escopo
                </button>
                <label className="termos-check">
                    <input type="checkbox" checked={onlyScope} onChange={(e) => setOnlyScope(e.target.checked)} />
                    só o escopo selecionado
                </label>
                <span className="termos-dica">
                    Escopo: <b>{escopoLabel(scope.company_id, scope.store_id)}</b>
                    {vigenteDoEscopo ? ` — vigente: v${vigenteDoEscopo.version}` : ' — sem versão vigente'}
                </span>
            </div>

            {loading && <p className="termos-vazio">Carregando…</p>}
            {!loading && lista.length === 0 && (
                <p className="termos-vazio">Nenhuma versão {onlyScope ? 'neste escopo' : 'cadastrada'}.</p>
            )}

            {lista.length > 0 && (
                <table className="termos-tabela">
                    <thead>
                        <tr>
                            <th>Escopo</th><th>Versão</th><th>Título</th><th>Status</th>
                            <th>Publicada em</th><th>Aceites</th><th />
                        </tr>
                    </thead>
                    <tbody>
                        {lista.map((v) => (
                            <tr key={v.id} className={edit?.id === v.id ? 'termos-linha-ativa' : ''}>
                                <td>{escopoLabel(v.company_id, v.store_id)}</td>
                                <td>v{v.version}</td>
                                <td>{v.title}</td>
                                <td><span className={`termos-tag termos-tag-${v.status}`}>{STATUS_LABEL[v.status] || v.status}</span></td>
                                <td>{fmtData(v.published_at)}</td>
                                <td>{v.accepted_users}</td>
                                <td><button className="termos-btn" onClick={() => abrir(v)}>{v.status === 'draft' ? 'Editar' : 'Ver'}</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {edit && (
                <div className="termos-editor">
                    <div className="termos-editor-col">
                        <h3>
                            {edit.id ? `${edit.readOnly ? 'Versão' : 'Rascunho'} v${edit.version || '(nova)'}` : 'Novo rascunho'}
                            {' — '}{escopoLabel(edit.company_id, edit.store_id)}
                        </h3>
                        {edit.readOnly && (
                            <p className="termos-aviso">
                                Versão {STATUS_LABEL[edit.status].toLowerCase()}: o texto não pode ser alterado (há aceites
                                apontando para ele). Para mudar, crie uma nova versão a partir desta.
                            </p>
                        )}
                        <label className="termos-campo">Título
                            <input type="text" maxLength={200} value={edit.title} disabled={edit.readOnly}
                                onChange={(e) => setEdit({ ...edit, title: e.target.value })} />
                        </label>
                        <label className="termos-campo">Texto (markdown-lite: **negrito**, _itálico_, quebras de linha)
                            <textarea rows={16} value={edit.body_md} disabled={edit.readOnly}
                                onChange={(e) => setEdit({ ...edit, body_md: e.target.value })} />
                        </label>
                        <p className="termos-dica">
                            Variáveis: <code>{'{{fullname}}'}</code> <code>{'{{company}}'}</code> <code>{'{{store}}'}</code> <code>{'{{cpf}}'}</code>.
                            O bloco <code>{'{{#cpf}}…{{/cpf}}'}</code> só aparece para quem tem CPF cadastrado.
                        </p>
                        {edit.sha && <p className="termos-dica">sha256 do texto: <code>{edit.sha}</code></p>}
                        <div className="termos-acoes">
                            {!edit.readOnly && (
                                <button className="termos-btn termos-btn-primario" disabled={saving} onClick={salvar}>
                                    {saving ? 'Salvando…' : 'Salvar rascunho'}
                                </button>
                            )}
                            {rascunhoSalvo && (
                                <button className="termos-btn termos-btn-ok" disabled={saving}
                                    onClick={() => setConfirm({ kind: 'publish', v: edit })}>
                                    Publicar…
                                </button>
                            )}
                            {rascunhoSalvo && (
                                <button className="termos-btn termos-btn-perigo" disabled={saving}
                                    onClick={() => setConfirm({ kind: 'delete', v: edit })}>
                                    Apagar rascunho
                                </button>
                            )}
                            {edit.readOnly && (
                                <button className="termos-btn termos-btn-primario"
                                    onClick={() => setEdit({ ...edit, id: null, status: 'draft', readOnly: false, version: null, sha: null,
                                        company_id: edit.company_id, store_id: edit.store_id })}>
                                    Nova versão a partir desta
                                </button>
                            )}
                            <button className="termos-btn" onClick={() => setEdit(null)}>Fechar</button>
                        </div>
                    </div>
                    <div className="termos-editor-col">
                        <h3>Prévia</h3>
                        <label className="termos-check">
                            <input type="checkbox" checked={simCpf} onChange={(e) => setSimCpf(e.target.checked)} />
                            simular usuário com CPF cadastrado
                        </label>
                        <div className="termos-previa"><BannerBody text={prev} /></div>
                        <p className="termos-dica">
                            Prévia do texto formatado, com dados de exemplo. Não simula a tela do app.
                        </p>
                    </div>
                </div>
            )}

            {confirm && (
                <Dialogo
                    titulo={confirm.kind === 'publish' ? 'Publicar versão' : 'Apagar rascunho'}
                    onCancelar={() => setConfirm(null)}
                    onConfirmar={executarConfirmacao}
                    rotuloConfirmar={confirm.kind === 'publish' ? 'Publicar' : 'Apagar'}
                    rotuloOcupado="Aguarde…"
                    ocupado={saving}
                    perigo={confirm.kind === 'delete'}
                >
                    {confirm.kind === 'publish' ? (
                        <p>
                            Publicar <b>{confirm.v.title}</b> em <b>{escopoLabel(confirm.v.company_id, confirm.v.store_id)}</b>?
                            {' '}A versão vigente deste escopo, se houver, será <b>aposentada</b>. O texto publicado não
                            pode mais ser editado. Isso não liga a exigência no app — quem controla é a política.
                        </p>
                    ) : (
                        <p>Apagar o rascunho <b>{confirm.v.title}</b>? Não dá para desfazer.</p>
                    )}
                </Dialogo>
            )}
        </div>
    );
}

/* ============================================================================================ */
/* Política                                                                                     */
/* ============================================================================================ */
function AbaPolitica({ scope, notify, escopoLabel, mesmoEscopo }) {
    const [policies, setPolicies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [valor, setValor] = useState('inherit');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await apiService.termsPoliciesList();
            setPolicies(r?.policies || []);
        } catch (e) { notify(erroDe(e, 'Erro ao carregar políticas'), 'error'); }
        finally { setLoading(false); }
    }, [notify]);
    useEffect(() => { load(); }, [load]);

    const doEscopo = policies.find((p) => mesmoEscopo(p.company_id, p.store_id));
    useEffect(() => { setValor(doEscopo ? doEscopo.enforcement : 'inherit'); }, [doEscopo]);

    const salvar = async () => {
        setSaving(true);
        try {
            await apiService.termsPolicySave({ ...scope, enforcement: valor === 'inherit' ? null : valor });
            notify('Política salva', 'success');
            await load();
        } catch (e) { notify(erroDe(e, 'Erro ao salvar política'), 'error'); }
        finally { setSaving(false); }
    };

    return (
        <div className="termos-corpo">
            <div className="termos-explica">
                <p><b>Sem política em nenhum nível = Desligado.</b> Publicar um texto não liga nada sozinho; tenant que
                    já assinou o termo físico continua como está até alguém ligar a política aqui. A política também
                    segue a cascata: loja &gt; empresa &gt; global.</p>
                <ul>
                    <li><b>Desligado</b> — nenhuma tela, nada é registrado.</li>
                    <li><b>Obrigatório (sem recusa)</b> — tela bloqueante só com “Aceito” e sair. Sem aceite, o vendedor
                        não entra no app.</li>
                    <li><b>Obrigatório com recusa registrada</b> — igual, mas a tela tem “Não aceito”: a recusa é gravada,
                        mostra o bloqueio e a pessoa pode voltar e aceitar depois (uma nova linha). Quem recusou também
                        é barrado na API ao iniciar gravação.</li>
                </ul>
            </div>

            <div className="termos-barra">
                <span className="termos-dica">Escopo: <b>{escopoLabel(scope.company_id, scope.store_id)}</b></span>
                <select value={valor} onChange={(e) => setValor(e.target.value)}>
                    <option value="inherit">Herdar (sem política própria)</option>
                    {Object.keys(ENFORCEMENT_LABEL).map((k) => <option key={k} value={k}>{ENFORCEMENT_LABEL[k]}</option>)}
                </select>
                <button className="termos-btn termos-btn-primario" disabled={saving} onClick={salvar}>
                    {saving ? 'Salvando…' : 'Salvar política deste escopo'}
                </button>
            </div>

            {loading && <p className="termos-vazio">Carregando…</p>}
            {!loading && policies.length === 0 && <p className="termos-vazio">Nenhuma política cadastrada: tudo desligado.</p>}
            {policies.length > 0 && (
                <table className="termos-tabela">
                    <thead><tr><th>Escopo</th><th>Política</th><th>Atualizada em</th></tr></thead>
                    <tbody>
                        {policies.map((p) => (
                            <tr key={p.id} className={mesmoEscopo(p.company_id, p.store_id) ? 'termos-linha-ativa' : ''}>
                                <td>{escopoLabel(p.company_id, p.store_id)}</td>
                                <td><span className={`termos-tag termos-enf-${p.enforcement}`}>{ENFORCEMENT_LABEL[p.enforcement] || p.enforcement}</span></td>
                                <td>{fmtData(p.updated)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

/* ============================================================================================ */
/* Relatório                                                                                    */
/* ============================================================================================ */
function AbaRelatorio({ scope, company, notify }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [filtro, setFiltro] = useState('todos');
    const [soAtivos, setSoAtivos] = useState(true);

    const carregar = useCallback(async () => {
        if (!scope.company_id) { setData(null); return; }
        setLoading(true);
        try {
            setData(await apiService.termsReport({ company_id: scope.company_id, store_id: scope.store_id }));
        } catch (e) { setData(null); notify(erroDe(e, 'Erro ao carregar relatório'), 'error'); }
        finally { setLoading(false); }
    }, [scope.company_id, scope.store_id, notify]);
    useEffect(() => { carregar(); }, [carregar]);

    const pessoas = useMemo(() => (data?.people || []).filter((p) => (
        (filtro === 'todos' || p.state === filtro) && (!soAtivos || p.is_active !== false)
    )), [data, filtro, soAtivos]);

    // contadores recalculados sobre o que a tabela mostra (respeita "só ativos")
    const contagem = useMemo(() => {
        const c = { accepted: 0, pending: 0, declined: 0, off: 0 };
        (data?.people || []).filter((p) => !soAtivos || p.is_active !== false).forEach((p) => { c[p.state] = (c[p.state] || 0) + 1; });
        return c;
    }, [data, soAtivos]);

    const exportar = () => {
        const cab = ['Vendedor', 'Usuário', 'Loja', 'Ativo', 'Estado', 'Política', 'Versão', 'Respondeu em', 'Último aceite'];
        const linhas = pessoas.map((p) => [
            p.fullname, p.username, p.store_name, p.is_active === false ? 'não' : 'sim',
            STATE_LABEL[p.state] || p.state, ENFORCEMENT_LABEL[p.enforcement] || p.enforcement,
            p.version != null ? `v${p.version}` : '', fmtData(p.responded_at), fmtData(p.last_accepted_at),
        ]);
        const slug = (company?.subdomain || 'empresa');
        baixarCsv(`termo-aceite-${slug}-${new Date().toISOString().slice(0, 10)}.csv`, [cab, ...linhas]);
    };

    if (!scope.company_id) {
        return <div className="termos-corpo"><p className="termos-vazio">Escolha uma empresa no topo para ver o relatório.</p></div>;
    }

    return (
        <div className="termos-corpo">
            <div className="termos-cards">
                {['accepted', 'pending', 'declined', 'off'].map((k) => (
                    <button key={k} className={`termos-card termos-card-${k} ${filtro === k ? 'termos-card-ativo' : ''}`}
                        onClick={() => setFiltro(filtro === k ? 'todos' : k)}>
                        <span className="termos-card-n">{contagem[k] || 0}</span>
                        <span className="termos-card-l">{STATE_LABEL[k]}</span>
                    </button>
                ))}
            </div>
            <div className="termos-barra">
                <select value={filtro} onChange={(e) => setFiltro(e.target.value)}>
                    <option value="todos">Todos os estados</option>
                    {Object.keys(STATE_LABEL).map((k) => <option key={k} value={k}>{STATE_LABEL[k]}</option>)}
                </select>
                <label className="termos-check">
                    <input type="checkbox" checked={soAtivos} onChange={(e) => setSoAtivos(e.target.checked)} />
                    só vendedores ativos
                </label>
                <button className="termos-btn" onClick={carregar} disabled={loading}>Atualizar</button>
                <button className="termos-btn termos-btn-primario" onClick={exportar} disabled={!pessoas.length}>Exportar CSV</button>
                <span className="termos-dica">{pessoas.length} vendedor(es) · {data?.company || ''}</span>
            </div>

            {loading && <p className="termos-vazio">Carregando…</p>}
            {!loading && data && pessoas.length === 0 && <p className="termos-vazio">Nenhum vendedor neste filtro.</p>}
            {pessoas.length > 0 && (
                <table className="termos-tabela">
                    <thead>
                        <tr><th>Vendedor</th><th>Usuário</th><th>Loja</th><th>Estado</th><th>Versão</th><th>Respondeu em</th><th>Último aceite</th></tr>
                    </thead>
                    <tbody>
                        {pessoas.map((p) => (
                            <tr key={`${p.user_id}-${p.store_id}`}>
                                <td>{p.fullname}{p.is_active === false ? ' (inativo)' : ''}</td>
                                <td>{p.username}</td>
                                <td>{p.store_name}</td>
                                <td><span className={`termos-tag termos-estado-${p.state}`}>{STATE_LABEL[p.state] || p.state}</span></td>
                                <td>{p.version != null ? `v${p.version}` : '—'}</td>
                                <td>{fmtData(p.responded_at)}</td>
                                <td>{fmtData(p.last_accepted_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
            <p className="termos-dica">
                O estado é calculado contra a versão que vale para a loja de cada vendedor. “Último aceite” é a data do
                aceite mais recente em qualquer versão.
            </p>
        </div>
    );
}

export default Termos;
